import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#DC2626", "#7C3AED", "#D97706"];

export default function KPICenterPage({ onLogout, userRole, isFeatureEnabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/kpi-center`, { headers: getAuthHeaders() }); setData(r.data); }
    catch { toast.error("Failed to load KPIs"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;
  if (!data) return null;

  const kpis = data.kpis || {};
  const barData = [
    { name: "Work Orders", completed: data.work_orders?.completed || 0, open: data.work_orders?.open || 0 },
    { name: "Maintenance", completed: data.maintenance?.completed || 0, overdue: data.maintenance?.overdue || 0 },
    { name: "CAP", closed: data.cap?.closed || 0, open: data.cap?.open || 0 },
    { name: "ECRs", total: data.ecm?.ecrs || 0, open: data.ecm?.open_ecrs || 0 },
  ];
  const pieData = [
    { name: "Blueprints", value: data.blueprints?.total || 0 },
    { name: "Config Items", value: data.config_mgmt?.total_cis || 0 },
    { name: "Suppliers", value: data.suppliers?.total || 0 },
    { name: "Qualifications", value: data.qualifications?.total || 0 },
    { name: "Audits", value: data.audits?.total || 0 },
  ].filter(d => d.value > 0);

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="kpi-center-page">
        <PageHeader title="Executive Analytics & KPI Center" subtitle="Plant-wide performance metrics across all modules" testId="kpi-title" />

        {/* Top KPIs */}
        <div className="grid grid-cols-3 gap-4">
          {[["WO Completion", kpis.wo_completion_rate, "%"], ["Maint. Effectiveness", kpis.maintenance_effectiveness, "%"], ["CAP Closure Rate", kpis.cap_closure_rate, "%"]].map(([label, val, unit]) => (
            <div key={label} className="bg-white border border-[#E2E8F0] p-5 text-center" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
              <p className="text-4xl font-heading font-black mt-1" style={{ color: val >= 80 ? "#10B981" : val >= 50 ? "#F59E0B" : "#DC2626" }}>{val}{unit}</p>
            </div>
          ))}
        </div>

        {/* Module Summaries */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="module-metrics">
          {[
            ["Open WOs", data.work_orders?.open, "#D97706"],
            ["Overdue Maint", data.maintenance?.overdue, "#DC2626"],
            ["Open ECRs", data.ecm?.open_ecrs, "#F59E0B"],
            ["Open SCARs", data.suppliers?.open_scars, "#DC2626"],
            ["Expired Quals", data.qualifications?.expired, "#DC2626"],
            ["Open Findings", data.audits?.open_findings, "#D97706"],
            ["Active Alts", data.config_mgmt?.active_alterations, "#2563EB"],
            ["Legal Holds", data.retention?.on_hold, "#DC2626"],
            ["Active Procs", data.procedures?.active_executions, "#10B981"],
            ["Open CAPs", data.cap?.open, "#D97706"],
            ["Expiring Quals", data.qualifications?.expiring_soon, "#F59E0B"],
            ["Pending Dest", data.retention?.pending_destruction, "#64748B"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-3 py-2.5">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p>
              <p className="text-xl font-heading font-black" style={{ color: (v || 0) > 0 ? c : "#CBD5E1" }}>{v || 0}</p>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="bar-chart">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-3">Module Activity</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="completed" fill="#10B981" name="Completed" />
                <Bar dataKey="closed" fill="#10B981" name="Closed" />
                <Bar dataKey="open" fill="#D97706" name="Open" />
                <Bar dataKey="overdue" fill="#DC2626" name="Overdue" />
                <Bar dataKey="total" fill="#2563EB" name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="pie-chart">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-3">Entity Distribution</p>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={{ stroke: "#94A3B8" }}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[220px] flex items-center justify-center text-sm text-[#94A3B8]">No entity data</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
